// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import type { AdminAccount, AdminReport, AdminRoom, DirectoryRoom, PasskeyInfo, RegistrationConfig, RegistrationToken, RoomEvent, RoomMember, Session, Sigchain, SigchainLink, TransparencyProof, TransparencyRoot, STR } from "./types";
import { homeserverBase } from "./server";

// DeviceInfo is one entry in the "logged-in devices" list.
export interface DeviceInfo {
  device_id: string;
  display_name: string;
  fingerprint?: string;
  created_at: number; // UnixMilli
  last_seen?: number; // UnixMilli
  current: boolean; // this is the device making the request
}

// Homeserver is a thin typed client over the C2S REST + WebSocket APIs. The base
// URL is resolved at request time from the user's server selection ("" = same
// origin; a public or self-hosted URL in the native app) — see server.ts. An
// explicit override may be passed for one-off calls.
export class Homeserver {
  constructor(private readonly override?: string) {}
  private get base(): string {
    return this.override ?? homeserverBase();
  }

  // onUnauthorized fires when an authenticated request is rejected (401) — e.g.
  // the token was revoked or the (memory-store) homeserver was restarted. The app
  // uses it to drop the stale session instead of showing authenticated content.
  private onUnauthorized?: () => void;
  setOnUnauthorized(fn: (() => void) | undefined) {
    this.onUnauthorized = fn;
  }

  private async post<T>(path: string, body: unknown, token?: string): Promise<T> {
    const res = await fetch(this.base + path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    return this.parse<T>(res);
  }

  private async get<T>(path: string, token: string): Promise<T> {
    const res = await fetch(this.base + path, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return this.parse<T>(res);
  }

  private async put<T>(path: string, body: unknown, token: string): Promise<T> {
    const res = await fetch(this.base + path, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    return this.parse<T>(res);
  }

  private async del<T>(path: string, token: string): Promise<T> {
    const res = await fetch(this.base + path, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return this.parse<T>(res);
  }

  private async parse<T>(res: Response): Promise<T> {
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) {
      if (res.status === 401) this.onUnauthorized?.();
      throw new Error((data && (data.error as string)) || `HTTP ${res.status}`);
    }
    return data as T;
  }

  // whoami validates the bearer token; rejects (401) if the session is invalid.
  whoami(token: string): Promise<{ user_id: string; device_id: string; admin?: boolean }> {
    return this.get("/client/v1/account/whoami", token);
  }

  // registrationConfig is the PUBLIC sign-up policy (no auth) — the register screen
  // reads it to decide whether to show the form (mode != "none") and whether to
  // require an invite token (mode == "invite").
  async registrationConfig(): Promise<RegistrationConfig> {
    const res = await fetch(this.base + "/client/v1/registration");
    return this.parse<RegistrationConfig>(res);
  }

  register(username: string, password: string, deviceName = "web", inviteToken = ""): Promise<Session> {
    return this.post("/client/v1/register", {
      username,
      password,
      device_name: deviceName,
      ...(inviteToken ? { invite_token: inviteToken } : {}),
    });
  }

  // --- registration invites (admin- or member-minted, per server policy) ---

  // mintRegistrationToken creates an invite; the secret is returned ONCE.
  mintRegistrationToken(token: string, uses = 0, ttlHours = -1): Promise<{ token: string; invite: RegistrationToken }> {
    return this.post("/client/v1/registration_tokens", { uses, ttl_hours: ttlHours }, token);
  }

  listRegistrationTokens(token: string): Promise<RegistrationToken[]> {
    return this.get("/client/v1/registration_tokens", token);
  }

  revokeRegistrationToken(token: string, id: string): Promise<unknown> {
    return this.del(`/client/v1/registration_tokens/${encodeURIComponent(id)}`, token);
  }

  // --- passkeys (WebAuthn) ---
  // The begin calls return the WebAuthn options + an opaque session handle; the
  // finish calls echo the handle in the query string so the BODY is the raw
  // credential the server's relying party parses.

  webauthnRegisterBegin(token: string): Promise<{ options: unknown; session: string }> {
    return this.post("/client/v1/webauthn/register/begin", {}, token);
  }

  webauthnRegisterFinish(token: string, session: string, name: string, credential: unknown): Promise<unknown> {
    const q = new URLSearchParams({ session, name }).toString();
    return this.post(`/client/v1/webauthn/register/finish?${q}`, credential, token);
  }

  // oidcLinkStart begins linking an OIDC identity to the logged-in account; returns
  // the provider URL the client redirects to (the callback links instead of creating).
  oidcLinkStart(token: string, provider: string): Promise<{ url: string }> {
    return this.post(`/client/v1/oidc/${encodeURIComponent(provider)}/link`, {}, token);
  }

  webauthnLoginBegin(): Promise<{ options: unknown; session: string }> {
    return this.post("/client/v1/webauthn/login/begin", {});
  }

  webauthnLoginFinish(session: string, deviceName: string, deviceId: string, credential: unknown): Promise<Session> {
    const q = new URLSearchParams({ session, device_name: deviceName, device_id: deviceId }).toString();
    return this.post(`/client/v1/webauthn/login/finish?${q}`, credential);
  }

  webauthnCredentials(token: string): Promise<PasskeyInfo[]> {
    return this.get("/client/v1/webauthn/credentials", token);
  }

  deleteWebauthnCredential(token: string, id: string): Promise<unknown> {
    return this.del(`/client/v1/webauthn/credentials/${encodeURIComponent(id)}`, token);
  }

  // --- moderation / admin console ---

  // report files a LOCAL abuse report against a message or room (any member). Since
  // messages are E2E-encrypted, the reporter (who can decrypt) attaches the plaintext
  // (content) + the message's author (targetSender) so moderators can see what was reported.
  report(token: string, roomID: string, eventID: string, reason: string, content = "", targetSender = ""): Promise<{ id: string }> {
    return this.post("/client/v1/report", { room_id: roomID, event_id: eventID, reason, content, target_sender: targetSender }, token);
  }

  adminReports(token: string, status = ""): Promise<AdminReport[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return this.get(`/client/v1/admin/reports${q}`, token);
  }

  adminResolveReport(token: string, id: string, action: "resolve" | "dismiss"): Promise<unknown> {
    return this.post(`/client/v1/admin/reports/${encodeURIComponent(id)}/resolve`, { action }, token);
  }

  adminDiscussReport(token: string, id: string, roomID: string): Promise<unknown> {
    return this.post(`/client/v1/admin/reports/${encodeURIComponent(id)}/discuss`, { room_id: roomID }, token);
  }

  adminAccounts(token: string): Promise<AdminAccount[]> {
    return this.get("/client/v1/admin/accounts", token);
  }

  adminSetAccountActive(token: string, userID: string, active: "deactivate" | "reactivate"): Promise<unknown> {
    return this.post(`/client/v1/admin/accounts/${encodeURIComponent(userID)}/${active}`, {}, token);
  }

  adminRooms(token: string): Promise<AdminRoom[]> {
    return this.get("/client/v1/admin/rooms", token);
  }

  adminPurgeRoom(token: string, roomID: string): Promise<unknown> {
    return this.del(`/client/v1/admin/rooms/${encodeURIComponent(roomID)}`, token);
  }

  // deviceId (optional) asks the server to re-use that already-provisioned device instead of
  // minting a new one — so re-logging on this machine keeps one stable device + key.
  login(username: string, password: string, deviceName = "web", deviceId = ""): Promise<Session> {
    return this.post("/client/v1/login", { username, password, device_name: deviceName, device_id: deviceId });
  }

  logout(token: string): Promise<unknown> {
    return this.post("/client/v1/logout", {}, token);
  }

  changePassword(token: string, oldPassword: string, newPassword: string): Promise<unknown> {
    return this.post("/client/v1/account/password", { old_password: oldPassword, new_password: newPassword }, token);
  }

  // --- account recovery (recovery codes) ---

  // generateRecoveryCodes mints a fresh set (replacing any existing); shown once.
  generateRecoveryCodes(token: string): Promise<{ codes: string[] }> {
    return this.post("/client/v1/account/recovery_codes", {}, token);
  }

  recoveryStatus(token: string): Promise<{ total: number; remaining: number }> {
    return this.get("/client/v1/account/recovery_codes", token);
  }

  // recoverPassword resets a forgotten password with an unused recovery code (no auth).
  recoverPassword(username: string, recoveryCode: string, newPassword: string): Promise<unknown> {
    return this.post("/client/v1/account/recover", { username, recovery_code: recoveryCode, new_password: newPassword });
  }

  // sfuStats returns the SFU's per-publisher bandwidth allocation for a room's call
  // (diagnostics; empty when the SFU is off or no SFU call is active).
  sfuStats(token: string, roomID: string): Promise<{ sfu: boolean; participants: { user_id: string; target_bitrate_bps: number; connection_state: string }[] | null }> {
    return this.get(`/client/v1/rooms/${encodeURIComponent(roomID)}/sfu_stats`, token);
  }

  // roomCall returns who is currently in a room's group call (seeds the indicator on
  // open, so a member can discover an in-progress call).
  async roomCall(roomID: string, token: string): Promise<string[]> {
    const r = await this.get<{ members: string[] }>(`/client/v1/rooms/${encodeURIComponent(roomID)}/call`, token);
    return r.members ?? [];
  }

  // clientConfig is the deployment-discovery endpoint: push + WebRTC ICE servers.
  clientConfig(token: string): Promise<{
    server_name: string;
    version?: string;
    push?: { enabled: boolean; ntfy_server?: string; fcm?: boolean };
    rtc?: { ice_servers?: { urls: string[]; username?: string; credential?: string }[]; ttl?: number; sfu?: boolean; simulcast?: boolean };
  }> {
    return this.get("/client/v1/config", token);
  }

  // directRooms is the m.direct marker — {counterparty-userID: [roomIDs]} — so DMs
  // sync across devices.
  getDirect(token: string): Promise<Record<string, string[]>> {
    return this.get("/client/v1/account/direct", token);
  }
  setDirect(token: string, map: Record<string, string[]>): Promise<unknown> {
    return this.put("/client/v1/account/direct", map, token);
  }

  // directoryRooms is the public channel directory (IRC /LIST).
  async directoryRooms(token: string): Promise<DirectoryRoom[]> {
    const r = await this.get<{ rooms: DirectoryRoom[] }>("/client/v1/directory/rooms", token);
    return r.rooms ?? [];
  }

  // joinedRooms lists the channels the user has joined.
  async joinedRooms(token: string): Promise<DirectoryRoom[]> {
    const r = await this.get<{ joined_rooms: DirectoryRoom[] }>("/client/v1/joined_rooms", token);
    return r.joined_rooms ?? [];
  }

  // invitedRooms lists rooms the user has a pending invite to (so a DM started while
  // they were offline still surfaces on next login). is_direct marks 1:1 DM invites.
  async invitedRooms(token: string): Promise<(DirectoryRoom & { is_direct?: boolean; inviter?: string })[]> {
    const r = await this.get<{ invited_rooms: (DirectoryRoom & { is_direct?: boolean; inviter?: string })[] }>(
      "/client/v1/invited_rooms",
      token,
    );
    return r.invited_rooms ?? [];
  }

  // resolveAlias maps "#name:server" to a room ID.
  async resolveAlias(token: string, alias: string): Promise<string> {
    const r = await this.get<{ room_id: string }>(`/client/v1/directory/room/${encodeURIComponent(alias)}`, token);
    return r.room_id;
  }

  async members(token: string, roomID: string): Promise<RoomMember[]> {
    const r = await this.get<{ members: RoomMember[] }>(
      `/client/v1/rooms/${encodeURIComponent(roomID)}/members`,
      token,
    );
    return r.members ?? [];
  }

  async messages(token: string, roomID: string, limit = 80): Promise<RoomEvent[]> {
    const r = await this.get<{ events: RoomEvent[] }>(
      `/client/v1/rooms/${encodeURIComponent(roomID)}/messages?limit=${limit}`,
      token,
    );
    return r.events ?? [];
  }

  async state(token: string, roomID: string): Promise<RoomEvent[]> {
    const r = await this.get<{ state: RoomEvent[] }>(
      `/client/v1/rooms/${encodeURIComponent(roomID)}/state`,
      token,
    );
    return r.state ?? [];
  }

  async keysQuery(token: string, users: string[]): Promise<Record<string, RoomMember["devices"]>> {
    const r = await this.get<{ device_keys: Record<string, RoomMember["devices"]> }>(
      `/client/v1/keys/query?users=${users.map(encodeURIComponent).join(",")}`,
      token,
    );
    return r.device_keys ?? {};
  }

  // ---- device sigchain / identity proofs ----------------------------------
  getSigchain(token: string, userID: string): Promise<Sigchain> {
    return this.get<Sigchain>(`/client/v1/sigchain/${encodeURIComponent(userID)}`, token);
  }
  setAnchor(token: string, anchorKey: string): Promise<unknown> {
    return this.post("/client/v1/sigchain/anchor", { anchor_key: anchorKey }, token);
  }
  appendSigchainLink(token: string, link: SigchainLink): Promise<unknown> {
    return this.post("/client/v1/sigchain/append", link, token);
  }
  // fetchProof fetches an identity-proof URL server-side (the browser can't, due
  // to CORS) so the client can confirm the external account published the proof.
  fetchProof(token: string, url: string): Promise<{ status: number; body: string }> {
    return this.get<{ status: number; body: string }>(`/client/v1/proofs/fetch?url=${encodeURIComponent(url)}`, token);
  }
  // mintInviteToken mints (or rotates) a room's invite-link token (+k). The token
  // is returned once — share it in the link; rotating invalidates older links.
  mintInviteToken(token: string, roomID: string): Promise<{ token: string; generation: number }> {
    return this.post<{ token: string; generation: number }>(`/client/v1/rooms/${encodeURIComponent(roomID)}/invite_token`, {}, token);
  }
  // joinViaToken joins a room by redeeming an invite-link token.
  joinViaToken(token: string, roomID: string, inviteToken: string): Promise<unknown> {
    return this.post(`/client/v1/rooms/${encodeURIComponent(roomID)}/join_via_token`, { token: inviteToken }, token);
  }

  // ---- media (E2E-encrypted blobs) ----------------------------------------
  // uploadMedia stores an already-encrypted blob, returning its content URI.
  async uploadMedia(token: string, ciphertext: ArrayBuffer): Promise<{ content_uri: string; media_id: string }> {
    const res = await fetch(this.base + "/client/v1/media/upload", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream", Authorization: `Bearer ${token}` },
      body: ciphertext,
    });
    return this.parse(res);
  }
  // downloadMedia fetches a blob's (opaque ciphertext) bytes by media ID.
  async downloadMedia(token: string, mediaID: string): Promise<ArrayBuffer> {
    const res = await fetch(this.base + `/client/v1/media/download/${encodeURIComponent(mediaID)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`media download ${res.status}`);
    return res.arrayBuffer();
  }

  // ---- key transparency ----------------------------------------------------
  // transparencyProof fetches a user's inclusion proof against the signed root.
  transparencyProof(token: string, userID: string): Promise<TransparencyProof> {
    return this.get<TransparencyProof>(`/client/v1/keys/transparency/proof/${encodeURIComponent(userID)}`, token);
  }

  // transparencyRoot fetches the current signed tree root (for witnessing/gossip).
  transparencyRoot(token: string): Promise<TransparencyRoot> {
    return this.get<TransparencyRoot>(`/client/v1/keys/transparency/root`, token);
  }

  // ---- ChanServ (channel services) ----------------------------------------
  // chanServRegister registers a channel with ChanServ (founder-gated).
  chanServRegister(token: string, roomID: string): Promise<unknown> {
    return this.post(`/client/v1/chanserv/register`, { room_id: roomID }, token);
  }
  // chanServAccess sets a user's persistent access level (op | voice | none).
  chanServAccess(token: string, roomID: string, userID: string, role: "op" | "voice" | "none"): Promise<unknown> {
    return this.post(`/client/v1/chanserv/access`, { room_id: roomID, user_id: userID, role }, token);
  }

  // ---- incoming webhooks (operator-gated) ---------------------------------
  // createWebhook mints a channel webhook; `url`/`path` are returned with the secret token
  // embedded (shown once). Prepend the homeserver origin to `path` to get the full URL.
  createWebhook(token: string, roomID: string, name: string): Promise<{ id: string; name: string; path: string; url: string }> {
    return this.post(`/client/v1/rooms/${encodeURIComponent(roomID)}/webhooks`, { name }, token);
  }
  listWebhooks(token: string, roomID: string): Promise<{ webhooks: { id: string; name: string }[] }> {
    return this.get(`/client/v1/rooms/${encodeURIComponent(roomID)}/webhooks`, token);
  }
  deleteWebhook(token: string, roomID: string, id: string): Promise<unknown> {
    return this.del(`/client/v1/rooms/${encodeURIComponent(roomID)}/webhooks/${encodeURIComponent(id)}`, token);
  }

  // ---- encrypted key backup (opaque blob; server never decrypts it) --------
  putKeyBackup(token: string, blob: string): Promise<unknown> {
    return this.put(`/client/v1/account/key_backup`, { blob }, token);
  }
  // getKeyBackup returns the stored blob, or null when there's no backup (404).
  async getKeyBackup(token: string): Promise<string | null> {
    try {
      const r = await this.get<{ blob: string }>(`/client/v1/account/key_backup`, token);
      return r.blob;
    } catch {
      return null;
    }
  }
  deleteKeyBackup(token: string): Promise<unknown> {
    return this.del(`/client/v1/account/key_backup`, token);
  }

  // ---- device manager (logged-in devices) ---------------------------------
  async devices(token: string): Promise<DeviceInfo[]> {
    const r = await this.get<{ devices: DeviceInfo[] }>(`/client/v1/devices`, token);
    return r.devices ?? [];
  }
  revokeDevice(token: string, deviceId: string): Promise<unknown> {
    return this.post(`/client/v1/devices/revoke`, { device_id: deviceId }, token);
  }
  renameDevice(token: string, deviceId: string, displayName: string): Promise<unknown> {
    return this.post(`/client/v1/devices/rename`, { device_id: deviceId, display_name: displayName }, token);
  }

  // pushConfig reports whether the homeserver offers push and which ntfy server to
  // register a topic on (+ whether FCM is available).
  pushConfig(token: string): Promise<{ enabled: boolean; ntfy_server?: string; fcm?: boolean }> {
    return this.get("/client/v1/push/config", token);
  }
  // registerPusher records this device's push registration (ntfy endpoint or FCM
  // token) so the server can wake it (content-free) while offline. The optional Web
  // Push keys let the server encrypt the wake-up so the relay sees only ciphertext.
  registerPusher(
    token: string,
    kind: "ntfy" | "fcm",
    endpoint: string,
    keys?: { p256dh: string; auth: string },
  ): Promise<unknown> {
    return this.post("/client/v1/pushers", { kind, endpoint, ...keys }, token);
  }
  deletePusher(token: string): Promise<unknown> {
    return this.del("/client/v1/pushers", token).catch(() => undefined);
  }
  // testPush asks the homeserver to send a content-free test wake-up to all of this user's own
  // devices (bypassing the offline check), to verify push from Settings.
  testPush(token: string): Promise<unknown> {
    return this.post("/client/v1/pushers/test", {}, token);
  }
  uploadKey(token: string, fingerprint: string, armoredPublicKey: string): Promise<unknown> {
    return this.post("/client/v1/keys/upload", { fingerprint, armored_public_key: armoredPublicKey }, token);
  }
}

// The client command frame is shared with the native client.
import type { ClientCommand } from "../../shared/types";
export type { ClientCommand };

// An ephemeral typing notice (not a room event).
export interface TypingFrame {
  room_id: string;
  user_id: string;
  typing: boolean;
}

// An ephemeral WebRTC signalling frame relayed between call peers (opaque content:
// {call_id, kind: "invite"|"answer"|"candidates"|"hangup", sdp?, candidates?}).
export interface SignalFrame {
  room_id: string;
  user_id: string;
  content: unknown;
}

// An ephemeral presence change (online/offline).
export interface PresenceFrame {
  user_id: string;
  online: boolean;
}

// An ephemeral read receipt: `user_id` has read up to `event_id` in `room_id`.
export interface ReceiptFrame {
  room_id: string;
  user_id: string;
  event_id: string;
}

interface SyncHandlers {
  onEvent: (ev: RoomEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (e: Event) => void;
  onServerError?: (msg: string) => void;
  onTyping?: (frame: TypingFrame) => void;
  onPresence?: (frame: PresenceFrame) => void;
  onSignal?: (frame: SignalFrame) => void;
  onCallMembers?: (frame: { room_id: string; members: string[] }) => void;
  onCallSuperseded?: (roomID: string) => void; // this call moved to another of my devices
  onPresenting?: (frame: { room_id: string; user_id: string; sharing: boolean; annotation: boolean }) => void; // a peer (un)shared their screen (+ whether they allow annotation)
  onCallMute?: (frame: { room_id: string; user_id: string; muted: boolean }) => void; // a peer (un)muted their mic
  onCallHand?: (frame: { room_id: string; user_id: string; raised: boolean }) => void; // a peer raised/lowered their hand
  onCallReaction?: (frame: { room_id: string; user_id: string; emoji: string }) => void; // a peer sent an emoji reaction
  onSfuSignal?: (frame: { room_id: string; content: unknown }) => void;
  // A whiteboard op relayed over the SFU fallback path (WB-005). `content` is the
  // opaque encoded op; `user_id` is the authenticated sender (anti-spoof).
  onWhiteboard?: (frame: { room_id: string; user_id: string; content: unknown }) => void;
  onReceipt?: (frame: ReceiptFrame) => void;
  // A co-member gossiped a signed tree root (key-transparency witnessing).
  onKeyTransparency?: (frame: { room_id: string; user_id: string; str: STR; server_key: string }) => void;
  // Fired when a dropped connection is about to be retried (the socket is down).
  onReconnecting?: (attempt: number) => void;
}

// SyncSocket wraps the authenticated /client/v1/sync WebSocket: it streams room
// events and accepts client commands, and AUTO-RECONNECTS with exponential
// backoff after an unexpected drop (network blip, server restart). A reconnected
// socket remembers nothing server-side, so subscriptions are replayed on reopen.
export class SyncSocket {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly handlers: SyncHandlers;
  private readonly subscriptions = new Set<string>();
  private closed = false; // true once close() is called — stops reconnecting
  private suspended = false; // true while backgrounded (mobile) — socket dropped so the user goes offline and push takes over
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private keepalive: ReturnType<typeof setInterval> | undefined; // app-level ping so idle sockets aren't reaped
  private testPaused = false; // test-only: hold the socket down (see *ForTest)

  constructor(token: string, handlers: SyncHandlers) {
    const base = homeserverBase();
    if (base) {
      // Selected server (native app / self-hosted): derive ws(s):// from http(s)://.
      this.url = `${base.replace(/^http/, "ws")}/client/v1/sync?access_token=${encodeURIComponent(token)}`;
    } else {
      const scheme = location.protocol === "https:" ? "wss" : "ws";
      this.url = `${scheme}://${location.host}/client/v1/sync?access_token=${encodeURIComponent(token)}`;
    }
    this.handlers = handlers;
    this.connect();
  }

  private connect(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      // A fresh connection has no server-side subscriptions — replay ours.
      for (const room of this.subscriptions) this.rawSend({ action: "subscribe", room_id: room });
      // App-level keepalive: during a call the socket carries no data (media is separate),
      // so without periodic upstream traffic an idle-timeout proxy/NAT reaps it ~60s in —
      // which tears down the server-side SFU peer. Ping every 25s to keep it warm.
      clearInterval(this.keepalive);
      this.keepalive = setInterval(() => this.rawSend({ action: "ping" } as unknown as ClientCommand), 25000);
      this.handlers.onOpen?.();
    };
    ws.onclose = () => {
      clearInterval(this.keepalive);
      this.handlers.onClose?.();
      this.scheduleReconnect();
    };
    ws.onerror = (e) => this.handlers.onError?.(e);
    ws.onmessage = (msg) => {
      try {
        const frame = JSON.parse(msg.data);
        if (frame.type === "sync.hello") return;
        if (frame.type === "error") {
          this.handlers.onServerError?.(String(frame.error ?? "server error"));
          return;
        }
        if (frame.type === "m.typing") {
          this.handlers.onTyping?.({ room_id: frame.room_id, user_id: frame.user_id, typing: !!frame.typing });
          return;
        }
        if (frame.type === "m.presence") {
          this.handlers.onPresence?.({ user_id: frame.user_id, online: !!frame.online });
          return;
        }
        if (frame.type === "m.receipt") {
          this.handlers.onReceipt?.({ room_id: frame.room_id, user_id: frame.user_id, event_id: frame.event_id });
          return;
        }
        if (frame.type === "m.key_transparency") {
          this.handlers.onKeyTransparency?.({ room_id: frame.room_id, user_id: frame.user_id, str: frame.str as STR, server_key: String(frame.server_key ?? "") });
          return;
        }
        if (frame.type === "m.call.signal") {
          this.handlers.onSignal?.({ room_id: frame.room_id, user_id: frame.user_id, content: frame.content });
          return;
        }
        if (frame.type === "m.call.members") {
          this.handlers.onCallMembers?.({ room_id: frame.room_id, members: (frame.members as string[]) ?? [] });
          return;
        }
        if (frame.type === "m.call.superseded") {
          this.handlers.onCallSuperseded?.(frame.room_id);
          return;
        }
        if (frame.type === "m.call.presenting") {
          this.handlers.onPresenting?.({ room_id: frame.room_id, user_id: frame.user_id, sharing: !!frame.sharing, annotation: !!frame.annotation });
          return;
        }
        if (frame.type === "m.call.hand") {
          this.handlers.onCallHand?.({ room_id: frame.room_id, user_id: frame.user_id, raised: !!frame.raised });
          return;
        }
        if (frame.type === "m.call.reaction") {
          this.handlers.onCallReaction?.({ room_id: frame.room_id, user_id: frame.user_id, emoji: String(frame.emoji ?? "") });
          return;
        }
        if (frame.type === "m.call.mute") {
          this.handlers.onCallMute?.({ room_id: frame.room_id, user_id: frame.user_id, muted: !!frame.muted });
          return;
        }
        if (frame.type === "m.sfu.signal") {
          this.handlers.onSfuSignal?.({ room_id: frame.room_id, content: frame.content });
          return;
        }
        if (frame.type === "m.whiteboard") {
          this.handlers.onWhiteboard?.({ room_id: frame.room_id, user_id: frame.user_id, content: frame.content });
          return;
        }
        this.handlers.onEvent(frame as RoomEvent);
      } catch {
        /* ignore non-JSON frames */
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.closed || this.testPaused || this.suspended || this.retryTimer) return;
    this.attempt += 1;
    // Exponential backoff (300ms → ~15s) with jitter to avoid a thundering herd.
    const delay = Math.min(15000, 300 * 2 ** Math.min(this.attempt - 1, 6)) + Math.random() * 300;
    this.handlers.onReconnecting?.(this.attempt);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (!this.closed) this.connect();
    }, delay);
  }

  private rawSend(cmd: ClientCommand): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(cmd));
  }

  send(cmd: ClientCommand): void {
    this.rawSend(cmd);
  }

  // sendSignal relays an opaque WebRTC signalling payload to the room's other peer.
  sendSignal(roomID: string, content: unknown): void {
    this.rawSend({ action: "signal", room_id: roomID, content } as unknown as ClientCommand);
  }

  // sendWhiteboard relays an opaque whiteboard op to the room (SFU fallback, WB-005).
  // 1:1/mesh calls use a P2P data channel instead; only the SFU backend uses this.
  sendWhiteboard(roomID: string, content: unknown): void {
    this.rawSend({ action: "whiteboard", room_id: roomID, content } as unknown as ClientCommand);
  }

  // sendSfuSignal relays SFU media signalling (offer/answer/candidates) to the server.
  sendSfuSignal(roomID: string, content: unknown): void {
    this.rawSend({ action: "sfu_signal", room_id: roomID, content } as unknown as ClientCommand);
  }

  // selectLayer tells the SFU which simulcast layer of `publisher`'s video to forward to us
  // (the tile-size hint): rid "f"/"h"/"q", or "" = auto. No-op off simulcast.
  selectLayer(roomID: string, publisher: string, layer: string): void {
    this.rawSend({ action: "sfu_layer", room_id: roomID, publisher, layer } as unknown as ClientCommand);
  }

  // callState reports whether this device is in the room's group call (server-tracked,
  // so the "Join call" indicator survives crashes + is discoverable by late joiners).
  callState(roomID: string, joined: boolean): void {
    this.rawSend({ action: "call_state", room_id: roomID, joined } as unknown as ClientCommand);
  }

  // presenting reports whether this device is screen-sharing into the room's call, so peers
  // render our tile as a shared screen (letterboxed + a badge). `annotation` carries whether
  // this presenter currently allows others to annotate the share (WB-008) — so a late joiner
  // learns the gate state from presence.
  presenting(roomID: string, sharing: boolean, annotation = false): void {
    this.rawSend({ action: "presenting", room_id: roomID, sharing, annotation } as unknown as ClientCommand);
  }

  // callMute reports this device's mic-muted state into the room's call, so peers show a
  // mic-off icon on our tile.
  callMute(roomID: string, muted: boolean): void {
    this.rawSend({ action: "call_mute", room_id: roomID, muted } as unknown as ClientCommand);
  }

  // raiseHand reports this device's raised/lowered hand into the room's call (ephemeral).
  raiseHand(roomID: string, raised: boolean): void {
    this.rawSend({ action: "call_hand", room_id: roomID, raised } as unknown as ClientCommand);
  }

  // sendCallReaction relays a transient emoji reaction to the room's call.
  sendCallReaction(roomID: string, emoji: string): void {
    this.rawSend({ action: "call_reaction", room_id: roomID, emoji } as unknown as ClientCommand);
  }

  subscribe(roomID: string): void {
    this.subscriptions.add(roomID); // remembered so it survives a reconnect
    this.rawSend({ action: "subscribe", room_id: roomID });
  }

  typing(roomID: string, typing: boolean): void {
    this.rawSend({ action: "typing", room_id: roomID, typing });
  }

  receipt(roomID: string, eventID: string): void {
    this.rawSend({ action: "receipt", room_id: roomID, event_id: eventID });
  }

  // witness gossips a signed tree root to a room's members for equivocation detection.
  witness(roomID: string, str: STR, serverKey: string): void {
    this.rawSend({ action: "witness", room_id: roomID, str, server_key: serverKey } as unknown as ClientCommand);
  }

  close(): void {
    this.closed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    clearInterval(this.keepalive);
    this.ws?.close();
  }

  // suspend drops the socket WITHOUT reconnecting — used when the mobile app is backgrounded, so
  // the server marks the user offline and push (the embedded ntfy service) takes over for new
  // messages/calls. resume() reconnects on foreground (and the catch-up runs on reopen).
  suspend(): void {
    if (this.suspended || this.closed) return;
    this.suspended = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    clearInterval(this.keepalive);
    this.ws?.close();
  }
  resume(): void {
    if (!this.suspended) return;
    this.suspended = false;
    this.connect();
  }
  get isSuspended(): boolean {
    return this.suspended;
  }

  // pauseForTest / resumeForTest exist only to exercise reconnect in tests
  // (Chromium's network emulation doesn't drop loopback WebSockets). They are
  // reachable solely via the DEV-only `window.__zalmoxisSync` handle.
  pauseForTest(): void {
    this.testPaused = true;
    this.handlers.onReconnecting?.(1);
    this.ws?.close();
  }
  resumeForTest(): void {
    this.testPaused = false;
    this.connect();
  }
}
